import {
  collection,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { err, ok, type Result } from '@shared/result';
import { asId, type EmergencyContactId, type PersonId } from '@shared/brand';
import type {
  CreateEmergencyContactInput,
  IEmergencyContactRepository,
  ILogger,
  RepositoryError,
  UpdateEmergencyContactInput,
} from '@application/ports';
import type { EmergencyContact } from '@domain/entities/Person';
import { COLLECTIONS, firebase } from '../firebase/client';
import { decodeEmergencyContact } from '../codecs/entities';
import { collectAllPages, runPagedQuery, translateFirestoreError } from './support';

/**
 * Emergency contacts, against Firestore.
 *
 * The adapter that was missing. The port, the codec, the security rules and the
 * whole profile screen already existed; the composition root wired the in-memory
 * fake into the production graph, so every number anybody saved went into a
 * JavaScript array and died with the tab. It reported success on the way, because
 * the fake returns the same `ok` a real write does.
 *
 * It survived review because `Services.contacts` was typed as the fake's concrete
 * class instead of this interface — the one service in the graph that was. Every
 * other one would have failed to compile.
 *
 * ---
 *
 * These are the only documents in the portal a client writes directly, and the
 * rules are correspondingly exact: the `personId` must be the caller's own, the
 * `areaId` must equal their claim, and `create` checks every field's type and
 * length. Two consequences shape what follows.
 *
 * **The `areaId` comes from the session, never from a form.** A mismatch is
 * refused by the rules, which surfaces as a flat `permission-denied` with nothing
 * to explain it — the caller passes it in `CreateEmergencyContactInput`, and it
 * has to be the one on the claim.
 *
 * **Every field the rules name has to be present on create.** `hasAll` fails a
 * document that omits one, so nothing here is conditional: an absent relation is
 * written as an empty string rather than left out.
 */
export class FirestoreEmergencyContactRepository implements IEmergencyContactRepository {
  constructor(private readonly logger: ILogger) {}

  async listFor(
    personId: PersonId,
  ): Promise<Result<readonly EmergencyContact[], RepositoryError>> {
    // Bounded by nature — a person lists two or three people — and ordered by the
    // preference the profile screen lets them arrange.
    return collectAllPages((page) =>
      runPagedQuery(
        collection(firebase().db, COLLECTIONS.emergencyContacts),
        [where('personId', '==', personId), orderBy('preferenceOrder')],
        page,
        decodeEmergencyContact,
        'emergencyContacts',
        (failures) => this.logger.warn('undecodable emergencyContact documents', { failures }),
      ),
    );
  }

  async create(
    input: CreateEmergencyContactInput,
  ): Promise<Result<EmergencyContactId, RepositoryError>> {
    try {
      const { db } = firebase();

      /*
       * The new contact goes last.
       *
       * `preferenceOrder` is who gets called first, so it is a real decision
       * rather than a sort key — and appending is the only safe default: putting
       * a new number ahead of the one someone deliberately placed first would
       * change who a hospital rings.
       */
      const existing = await this.listFor(input.personId);
      const nextOrder = existing.ok
        ? existing.value.reduce((highest, contact) => Math.max(highest, contact.preferenceOrder), -1) + 1
        : 0;

      const ref = doc(collection(db, COLLECTIONS.emergencyContacts));

      // Every key the `create` rule lists, unconditionally — see the note above.
      await setDoc(ref, {
        personId: input.personId,
        areaId: input.areaId,
        name: input.name.trim(),
        relation: input.relation.trim(),
        phone: input.phone.trim(),
        preferenceOrder: nextOrder,
        schemaVersion: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return ok(asId<'EmergencyContactId'>(ref.id));
    } catch (cause) {
      return err(translateFirestoreError(cause, 'emergencyContacts create'));
    }
  }

  async update(
    contactId: EmergencyContactId,
    patch: UpdateEmergencyContactInput,
  ): Promise<Result<void, RepositoryError>> {
    try {
      const { db } = firebase();

      /*
       * Only the four fields the update rule allows, and only the ones asked for.
       *
       * `onlyFields` compares the affected keys against its list, so sending an
       * unchanged `personId` — harmless as a write — is refused outright.
       */
      const changes: Record<string, unknown> = { updatedAt: serverTimestamp() };
      if (patch.name !== undefined) changes['name'] = patch.name.trim();
      if (patch.relation !== undefined) changes['relation'] = patch.relation.trim();
      if (patch.phone !== undefined) changes['phone'] = patch.phone.trim();
      if (patch.preferenceOrder !== undefined) {
        changes['preferenceOrder'] = Math.max(0, Math.trunc(patch.preferenceOrder));
      }

      await updateDoc(doc(db, COLLECTIONS.emergencyContacts, contactId), changes);
      return ok(undefined);
    } catch (cause) {
      return err(translateFirestoreError(cause, `emergencyContacts/${contactId}`));
    }
  }

  async delete(contactId: EmergencyContactId): Promise<Result<void, RepositoryError>> {
    try {
      await deleteDoc(doc(firebase().db, COLLECTIONS.emergencyContacts, contactId));
      return ok(undefined);
    } catch (cause) {
      return err(translateFirestoreError(cause, `emergencyContacts/${contactId}`));
    }
  }

  /**
   * Rewrites the calling order in one batch.
   *
   * A batch rather than a loop of writes because a half-applied reorder leaves two
   * contacts claiming the same position, and the list would then render in an
   * order nobody chose.
   */
  async reorder(
    personId: PersonId,
    orderedIds: readonly EmergencyContactId[],
  ): Promise<Result<void, RepositoryError>> {
    try {
      const { db } = firebase();
      const batch = writeBatch(db);

      orderedIds.forEach((contactId, index) => {
        batch.update(doc(db, COLLECTIONS.emergencyContacts, contactId), {
          preferenceOrder: index,
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();
      return ok(undefined);
    } catch (cause) {
      this.logger.warn('emergency contact reorder failed', { personId });
      return err(translateFirestoreError(cause, 'emergencyContacts reorder'));
    }
  }
}
